import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { StyledButton } from '~/components/elements/StyledButton'
import { SuspenseDialog } from '~/components/elements/SuspenseDialog'
import { SurveyFilterGroupsInput } from '~/components/features/SurveyFilterGroupsInput'
import { type InputValues } from '~/components/features/SurveyFilterGroupsInput/utils'
import {
  useSurveyQueryBrandGroups,
  useSurveyQueryCustomerAttributeGroups,
  useSurveyQueryQuestions,
} from '~/hooks/useSurveyQuery'
import { type SurveyFilterGroups, surveyFilterGroupsSchema } from '~/utils'

const surveyFilterItemSchema = surveyFilterGroupsSchema.element.element

// Resolver schema: block submit while any added row is schema-invalid, but keep
// the untouched "全データ" path valid. An empty group ([]) is allowed (it
// normalizes to [] = all data); a non-empty group requires every row to satisfy
// the item schema. We intentionally drop the surveyFilterGroupsSchema's inner
// `.min(1)` so the default [[]] (one empty group) passes validation.
const resolver = zodResolver(
  z.object({ filterGroups: z.array(z.array(surveyFilterItemSchema)) }),
)

type EditFilterPatternDialogProps = {
  filterGroups: SurveyFilterGroups
  onClose: () => void
  onConfirm: (filterGroups: SurveyFilterGroups) => void
}

export const EditFilterPatternDialog = ({
  filterGroups,
  onClose,
  onConfirm,
}: EditFilterPatternDialogProps) => {
  const brandGroups = useSurveyQueryBrandGroups()
  const customerAttributeGroups = useSurveyQueryCustomerAttributeGroups()
  const questions = useSurveyQueryQuestions()
  const { t } = useTranslation()

  const { control, handleSubmit } = useForm<InputValues>({
    defaultValues: {
      filterGroups: filterGroups.length === 0 ? [[]] : filterGroups,
    },
    resolver,
  })

  const handleConfirm = React.useCallback(
    (values: InputValues) => {
      // Normalize before persisting: drop schema-invalid items and empty groups
      // so an untouched "全データ" edit collapses [[]] back to [] and incomplete
      // filters are not saved. (Intentional divergence from local, which saved raw form state.)
      const normalized = values.filterGroups
        .map((group) =>
          group.filter(
            (item) => surveyFilterItemSchema.safeParse(item).success,
          ),
        )
        .filter((group) => group.length > 0)
      onConfirm(normalized)
    },
    [onConfirm],
  )

  return (
    <SuspenseDialog
      actions={
        <>
          <StyledButton buttonStyle="light" onClick={onClose}>
            {t('basic_output_filter_dialog_cancel')}
          </StyledButton>
          <StyledButton
            buttonStyle="success"
            onClick={handleSubmit(handleConfirm)}
          >
            {t('basic_output_filter_dialog_confirm')}
          </StyledButton>
        </>
      }
      title={t('basic_output_filter_dialog_title')}
      width={800}
      onClose={onClose}
    >
      <SurveyFilterGroupsInput
        brandGroups={brandGroups}
        control={control}
        customerAttributeGroups={customerAttributeGroups}
        questions={questions}
      />
    </SuspenseDialog>
  )
}
